// import { DynamicSelect } from "../../../components/Inputs/Inputs";
import PageTitle from "../../../components/PageTitle/PageTitle";
import AlertModal from "../../../components/AlertModal/AlertModal";
import { AuthContext } from "../../../contexts/AuthContext";
import { useContext, useCallback, useState, useEffect, useMemo } from "react";
import "./MySessionsAndMessages.scss";
import { ReactComponent as User } from "../../../assets/user.svg";
import { ReactComponent as Loader } from "../../../assets/loader.svg";
import { ReactComponent as Download } from "../../../assets/download.svg";
import Pagination from "../../../components/Pagination/Pagination";
import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import { DynamicInput } from "../../../components/Inputs/Inputs";

export default function MySessionsAndMessages() {
  const {
    axiosAuth,
    accessibleGroups = [],
    accessibleHotels = [],
  } = useContext(AuthContext);
  // dados base
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  // tipo de resposta por sessão: 'ia' | 'fallback' | 'pendente'
  const [sessionAnswerType, setSessionAnswerType] = useState({});

  // ui
  const [loading, setLoading] = useState(null);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  // paginação
  const [page, setPage] = useState(1);
  const limit = 20;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.ceil(total / limit), [total]);

  // feature de download
  const [downloadingSession, setDownloadingSession] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [downloadingSessionsZip, setDownloadingSessionsZip] = useState(false);

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  // carregar grupos
  const fetchGroups = useCallback(async () => {
    if (accessibleGroups.length === 0) return;
    try {
      const data = Array.isArray(accessibleGroups) ? accessibleGroups : [];
      setGroups(data);

      // se houver exatamente 1 grupo e ainda não há seleção, seleciona-o
      if (!selectedGroupId && data.length === 1) {
        setSelectedGroupId(String(data[0].id));
      }
    } catch (err) {
      openError(
        "Erro ao carregar grupos",
        err?.response?.data?.detail || err.message
      );
    }
  }, [axiosAuth, openError, selectedGroupId, accessibleGroups]);

  // carregar sessões
  const fetchSessions = useCallback(async () => {
    if (accessibleGroups.length === 0 || !selectedGroupId) return;
    setLoading(true);
    try {
      const params = {
        group_id: Number(selectedGroupId),
        skip: (page - 1) * limit,
        limit,
      };
      if (selectedHotelId) params.hotel_id = Number(selectedHotelId);
      const { data } = await axiosAuth.get("/chat-sessions/", { params });

      // estrutura: {results, total}
      if (
        data &&
        Array.isArray(data.results) &&
        typeof data.total === "number"
      ) {
        setSessions(data.results);
        setTotal(data.total);
        return;
      }

      // falback simples: filtra por group_id aqui no front
      if (Array.isArray(data)) {
        const filtered = data.filter((s) => String(selectedGroupId));
        setSessions(
          filtered.slice((page - 1) * limit, (page - 1) * limit + limit)
        );
        setTotal(filtered.length);
        return;
      }

      // falback: objeto sem results/total mas com lista numa prop conhecida
      const items = Array.isArray(data.items) ? data.items : [];
      const filtered = items.filter(
        (s) => String(s.group_id) === String(selectedGroupId)
      );
      setSessions(
        filtered.slice((page - 1) * limit, (page - 1) * limit + limit)
      );
      setTotal(filtered.length);
    } catch (err) {
      openError(
        "Erro ao carregar sessões",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedGroupId, selectedHotelId, page]);

  // carregar mensagens
  const fetchMessages = useCallback(async () => {
    if (!selectedGroupId || !selectedSessionId) {
      setMessages([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosAuth.get(
        `/chat-messages/session/${selectedSessionId}`
      );
      setMessages({ data });

      // normalização de formatos de resposta
      let raw = [];
      if (Array.isArray(data)) raw = data;
      else if (Array.isArray(data?.results)) raw = data.results;
      else if (Array.isArray(data?.items)) raw = data.items;

      // ordenar do mais antigo para o mais recente
      const sorted = [...raw].sort((a, b) => {
        const da = new Date(
          a.created_at || a.timestamp || a.date || 0
        ).getTime();
        const db = new Date(
          b.created_at || b.timestamp || b.date || 0
        ).getTime();
        return da - db;
      });
      setMessages(sorted);
    } catch (err) {
      openError(
        "Erro ao carregar mensagens",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedGroupId, selectedSessionId, openError]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // sempre que muda de grupo, volta à página 1, limpa sessão/mensagens e o filtro de hotel
  useEffect(() => {
    setPage(1);
    setSelectedSessionId("");
    setMessages([]);
    setSelectedHotelId("");
  }, [selectedGroupId]);

  // sempre que muda o filtro de hotel, volta à página 1
  useEffect(() => {
    setPage(1);
  }, [selectedHotelId]);

  // sempre que muda de sessão, carrega mensagens
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // ao entrar / trocar de cliente, abre automaticamente a sessão mais recente
  useEffect(() => {
    if (page !== 1) return;
    if (selectedSessionId) return;
    if (!sessions.length) return;
    const mostRecent = [...sessions].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return db - da;
    })[0];
    if (mostRecent) setSelectedSessionId(String(mostRecent.id));
  }, [sessions, page, selectedSessionId]);

  // para cada sessão visível, determinar como foi respondido: 'ia' | 'fallback'
  // | 'pendente' (a partir das mensagens do agente / is_fallback)
  useEffect(() => {
    if (!sessions.length) return;
    let cancelled = false;

    (async () => {
      const toFetch = sessions.filter(
        (s) => sessionAnswerType[s.id] === undefined
      );
      if (!toFetch.length) return;

      const results = await Promise.all(
        toFetch.map(async (s) => {
          try {
            const { data } = await axiosAuth.get(
              `/chat-messages/session/${s.id}`
            );
            let raw = [];
            if (Array.isArray(data)) raw = data;
            else if (Array.isArray(data?.results)) raw = data.results;
            else if (Array.isArray(data?.items)) raw = data.items;

            const agentMsgs = raw.filter((m) => m.origin === "agent");
            let type = "pendente";
            if (agentMsgs.some((m) => m.is_fallback)) type = "fallback";
            else if (agentMsgs.length) type = "ia";
            return [s.id, type];
          } catch {
            return [s.id, null];
          }
        })
      );

      if (!cancelled) {
        setSessionAnswerType((prev) => {
          const next = { ...prev };
          results.forEach(([id, type]) => {
            next[id] = type;
          });
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessions, axiosAuth, sessionAnswerType]);

  const groupOptions = useMemo(() =>
    groups.map((g) => ({ value: String(g.id), label: g.name }))
  );

  const hotelOptions = useMemo(
    () => accessibleHotels.map((h) => ({ value: String(h.id), label: h.name })),
    [accessibleHotels]
  );

  // sessão atual selecionada (para o header do chat)
  const currentSession = useMemo(
    () => sessions.find((s) => String(s.id) === String(selectedSessionId)),
    [sessions, selectedSessionId]
  );

  const handleDownloadSession = useCallback(async () => {
    if (!selectedSessionId) return;

    setDownloadingSession(true);

    try {
      const response = await axiosAuth.get(
        `/chat-sessions/${selectedSessionId}/export`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const baseName = currentSession?.session_name || selectedSessionId;
      const safeName = String(baseName).replace(/[^\w\d_-]+/g, "_");

      link.href = url;
      link.download = `session-${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      openError(
        "Erro ao transferir sessão",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setDownloadingSession(false);
    }
  }, [axiosAuth, selectedSessionId, currentSession, openError]);

  const handleExportDateChange = useCallback((name, value) => {
    if (name === "date_from") setExportDateFrom(value);
    if (name === "date_to") setExportDateTo(value);
  });

  const handleDownloadSessionsRange = useCallback(async () => {
    if (!selectedGroupId) {
      openError(
        "Validação",
        "Tem de selecionar um grupo para exportar sessões"
      );
      return;
    }

    if (!exportDateFrom || !exportDateTo) {
      openError("Validação", "Tem de escolher ambas as datas.");
      return;
    }

    if (exportDateFrom > exportDateTo) {
      openError(
        "Validação",
        "A data inicial não pode ser posterior à data final."
      );
      return;
    }

    setDownloadingSessionsZip(true);

    try {
      const params = {
        group_id: Number(selectedGroupId),
        date_from: exportDateFrom,
        date_to: exportDateTo,
      };
      if (selectedHotelId) params.hotel_id = Number(selectedHotelId);
      const response = await axiosAuth.get("/chat-sessions/export/range", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const safeGroup = String(selectedGroupId).replace(/[^\w\d_-]+/g, "_");
      const fileName = `sessions-${safeGroup}-${exportDateFrom}_${exportDateTo}.zip`;

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // fecha o modal após a transferencia
      setExportModalVisible(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        openError(
          "Erro ao transferir sessões",
          "Não há sessões dentro dessa janela de datas."
        );
      } else {
        openError(
          "Erro ao transferir sessões",
          err?.response?.data?.detail || err.message
        );
      }
    } finally {
      setDownloadingSessionsZip(false);
    }
  }, [
    axiosAuth,
    selectedGroupId,
    selectedHotelId,
    exportDateFrom,
    exportDateTo,
    openError,
  ]);

  // helpers para mapear campos variados de mensagens
  const getRole = (m) =>
    (m.origin || "").toLowerCase().includes("agent")
      ? "agent"
      : (m.role || m.sender || m.source || "").toLowerCase().includes("visitor")
      ? "visitor"
      : m.is_agent
      ? "agent"
      : "visitor";

  const getText = (m) => m.content ?? null;

  const getCreatedAt = (m) => m.timestamp ?? null;

  return (
    <div className="sessions-and-messages-page client">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      <div className="title-section">
        <PageTitle title="Sessões & mensagens" />
        <Button
          type="button"
          onClick={() => setExportModalVisible(true)}
          disabled={!selectedGroupId}
        >
          Transferir sessões
        </Button>
      </div>
      <div className="container">
        <div className="left-side">
          {hotelOptions.length > 1 && (
            <div className="client-selector">
              <div id="dynamic-select-wrapper">
                <label className="dynamic-select-label">Hotel</label>
                <select
                  className="dynamic-select-field"
                  value={selectedHotelId}
                  disabled={loading}
                  onChange={(e) => setSelectedHotelId(e.target.value)}
                >
                  <option value="">Todos os hotéis</option>
                  {hotelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="sessions-selector">
            <div className="sessions">
              {sessions.map((s) => {
                const isActive = String(s.id) === String(selectedSessionId);
                return (
                  <button
                    className={`each-session ${isActive ? "active" : ""}`}
                    key={s.id}
                    onClick={() => setSelectedSessionId(String(s.id))}
                  >
                    <div className="avatar">
                      <User />
                    </div>
                    <div className="content">
                      <p className="session-name">{s.session_name}</p>
                      <div className="session-meta">
                        <span className="session-date">
                          {s.created_at
                            ? new Date(s.created_at).toLocaleDateString()
                            : "--/--/----"}
                        </span>
                        {sessionAnswerType[s.id] &&
                          sessionAnswerType[s.id] !== "pendente" && (
                            <span
                              className={`answer-badge ${
                                sessionAnswerType[s.id]
                              }`}
                            >
                              {sessionAnswerType[s.id] === "fallback"
                                ? "Fallback"
                                : "Resolvida por IA"}
                            </span>
                          )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {total > limit && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p)}
              />
            )}
          </div>
        </div>
        <div className="right-side">
          <div className="chat">
            <div className="chat-header">
              <div className="avatar-name">
                <div className="avatar">
                  <User />
                </div>
                <div className="name">
                  <p>
                    {currentSession?.session_name ??
                      "Nenhuma sessão selecionada"}
                  </p>
                </div>
                {selectedSessionId && (
                  <div className="download">
                    <button
                      type="button"
                      onClick={handleDownloadSession}
                      disabled={downloadingSession}
                    >
                      <Download />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="chat-body">
              {!selectedSessionId && (
                <div className="empty-state">
                  <Loader />
                  <p>Selecione uma sessão para ver as mensagens.</p>
                </div>
              )}

              {selectedSessionId && messages.length === 0 && !loading && (
                <div className="empty-state">
                  <p>Sem mensagens nesta sessão.</p>
                </div>
              )}

              {selectedSessionId &&
                messages.map((m, idx) => {
                  const role = getRole(m);
                  const text = getText(m);
                  const dateStr = getCreatedAt(m)
                    ? new Date(getCreatedAt(m)).toLocaleString()
                    : "";
                  const cls =
                    role === "agent" ? "agent-message" : "visitor-message";
                  return (
                    <div className={cls} key={m.id || idx}>
                      <div className="message">
                        <p>{text}</p>
                      </div>
                      <div className="date">
                        <span>{dateStr}</span>
                      </div>
                    </div>
                  );
                })}

              {loading && (
                <div className="loading">
                  <p>A carregar...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {
        <Modal
          title="Transferir sessões"
          saveText="Transferir"
          deleteText="Fechar"
          isVisible={exportModalVisible}
          onClose={() => {
            if (downloadingSessionsZip) return;
            setExportModalVisible(false);
          }}
          onSave={handleDownloadSessionsRange}
          disableActions={downloadingSessionsZip}
        >
          <div className="row two-col">
            <DynamicInput
              type="date"
              label="Data inicial"
              name="date_from"
              value={exportDateFrom}
              onChange={handleExportDateChange}
              required
              disabled={downloadingSessionsZip}
              max={exportDateTo || undefined}
            />
            <DynamicInput
              type="date"
              label="Data final"
              name="date_to"
              value={exportDateTo}
              onChange={handleExportDateChange}
              required
              disabled={downloadingSessionsZip}
              min={exportDateFrom || undefined}
            />
          </div>
        </Modal>
      }
    </div>
  );
}
